/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useNavigate, useLocation } from "react-router";
import { PrimaryButton, SearchInput, SecondaryButton } from "../../components";
import { useUsers } from "../../store/useUsers";
import { useEffect, useLayoutEffect, useMemo, useState, useRef } from "react";
import { UsersTable } from "./UsersTable";
import { useMarketPricing } from "../../store/useMarketPricing";
import { KMeanEnum } from "../../utils/enums";
import {
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";

let searchTimeout: string | number | NodeJS.Timeout | null | undefined = null;

function UsersScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    getAllUsers,
    hasNextPage,
    allUsers,
    searchText,
    setSearchText,
    downloadUsersInCSV,
    allUsersLoading,
  } = useUsers();
  const { marketPrices, getAllMarketPrices } = useMarketPricing();
  /** State */
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showSuperAdmin, setShowSuperAdmin] = useState(false);
  const [showStoreAdmin, setShowStoreAdmin] = useState(false);
  const [showManager, setShowManager] = useState(false);
  const [showIOS, setShowIOS] = useState(false);
  const [showAndroid, setShowAndroid] = useState(false);
  const [showBiggestRevenuers, setShowBiggestRevenuers] = useState(false);
  const [showBiggestVolumeUsers, setShowBiggestVolumeUsers] = useState(false);
  const [showUsersWithoutPostalcode, setShowUsersWithoutPostalcode] =
    useState(false);
  const [
    showUsersWithoutDropsInLast5Days,
    setShowUsersWithoutDropsInLast5Days,
  ] = useState(false);
  const [specificMaterials, setSpecificMaterials] = useState<string[]>([]);
  const [specificKMeans, setSpecificKMeans] = useState<number[]>([]);
  const [dropCountFilter, setDropCountFilter] = useState<string>("");
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const filtersLoadedFromUrl = useRef(false);
  const [advancedParent] = useAutoAnimate<HTMLDivElement>();

  const activeFilters = useMemo(() => {
    const filters: { key: string; label: string; clear: () => void }[] = [];

    if (showAdmin)
      filters.push({
        key: "role-admin",
        label: "Admin",
        clear: () => setShowAdmin(false),
      });
    if (showSuperAdmin)
      filters.push({
        key: "role-super-admin",
        label: "Super admin",
        clear: () => setShowSuperAdmin(false),
      });
    if (showStoreAdmin)
      filters.push({
        key: "role-store-admin",
        label: "Store admin",
        clear: () => setShowStoreAdmin(false),
      });
    if (showManager)
      filters.push({
        key: "role-manager",
        label: "Manager",
        clear: () => setShowManager(false),
      });

    if (showIOS)
      filters.push({
        key: "platform-ios",
        label: "iOS",
        clear: () => setShowIOS(false),
      });
    if (showAndroid)
      filters.push({
        key: "platform-android",
        label: "Android",
        clear: () => setShowAndroid(false),
      });

    if (showBiggestRevenuers)
      filters.push({
        key: "sort-revenue",
        label: "Top revenue",
        clear: () => setShowBiggestRevenuers(false),
      });
    if (showBiggestVolumeUsers)
      filters.push({
        key: "sort-volume",
        label: "Top volume",
        clear: () => setShowBiggestVolumeUsers(false),
      });

    if (showUsersWithoutPostalcode)
      filters.push({
        key: "no-postal",
        label: "No postal code",
        clear: () => setShowUsersWithoutPostalcode(false),
      });
    if (showUsersWithoutDropsInLast5Days)
      filters.push({
        key: "no-drops-5-days",
        label: "No drops in last 5 days",
        clear: () => setShowUsersWithoutDropsInLast5Days(false),
      });

    if (dropCountFilter === "never")
      filters.push({
        key: "drops-never",
        label: "Never dropped",
        clear: () => setDropCountFilter(""),
      });
    if (dropCountFilter === "onePlus")
      filters.push({
        key: "drops-1-plus",
        label: "1+ drops",
        clear: () => setDropCountFilter(""),
      });
    if (dropCountFilter === "twoPlus")
      filters.push({
        key: "drops-2-plus",
        label: "2+ drops",
        clear: () => setDropCountFilter(""),
      });
    if (dropCountFilter === "threePlus")
      filters.push({
        key: "drops-3-plus",
        label: "3+ drops",
        clear: () => setDropCountFilter(""),
      });

    specificKMeans.forEach((value) => {
      const label = (KMeanEnum as any)?.[value] ?? `Segment ${value}`;
      filters.push({
        key: `kmeans-${value}`,
        label,
        clear: () =>
          setSpecificKMeans((prev) => prev.filter((item) => item !== value)),
      });
    });

    specificMaterials.forEach((id) => {
      const materialName =
        marketPrices?.find((m) => m._id === id)?.name ?? "Material";
      filters.push({
        key: `material-${id}`,
        label: materialName,
        clear: () =>
          setSpecificMaterials((prev) => prev.filter((item) => item !== id)),
      });
    });

    return filters;
  }, [
    showAdmin,
    showSuperAdmin,
    showStoreAdmin,
    showManager,
    showIOS,
    showAndroid,
    showBiggestRevenuers,
    showBiggestVolumeUsers,
    showUsersWithoutPostalcode,
    showUsersWithoutDropsInLast5Days,
    dropCountFilter,
    specificKMeans,
    specificMaterials,
    marketPrices,
  ]);

  /** Callbacks */
  const addUser = () => navigate(`/add-user`);
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(e.target.value);

  const userFilters = useMemo(() => {
    let filters = {};
    if (specificKMeans?.length) {
      filters = { ...filters, kMeans: specificKMeans };
    }

    const selectedRoles = [];
    if (showAdmin) selectedRoles.push(1);
    if (showSuperAdmin) selectedRoles.push(7);
    if (showManager) selectedRoles.push(2);
    if (showStoreAdmin) selectedRoles.push(5);

    if (selectedRoles.length > 0) {
      filters = {
        ...filters,
        user_role:
          selectedRoles.length === 1 ? selectedRoles[0] : selectedRoles,
      };
    }

    if (showIOS && showAndroid) {
      filters = { ...filters, platform: ["ios", "android"] };
    } else {
      if (showIOS) filters = { ...filters, platform: "ios" };
      if (showAndroid) filters = { ...filters, platform: "android" };
    }
    return filters;
  }, [
    showAdmin,
    showSuperAdmin,
    showManager,
    showStoreAdmin,
    showIOS,
    showAndroid,
    specificKMeans,
  ]);

  const userSort = useMemo(() => {
    if (showBiggestRevenuers && showBiggestVolumeUsers)
      return {
        totalRevenue: "desc",
        dropsVolume: "desc",
        tomraDropsVolume: "desc",
      };
    if (showBiggestRevenuers) return { totalRevenue: "desc" };
    if (showBiggestVolumeUsers)
      return { dropsVolume: "desc", tomraDropsVolume: "desc" };
    return { createdAt: "desc" };
  }, [showBiggestRevenuers, showBiggestVolumeUsers]);

  /** Hydrate filters + search from URL before paint so query string is never “too late”. */
  useLayoutEffect(() => {
    if (filtersLoadedFromUrl.current) return;

    const searchParams = new URLSearchParams(location.search);
    const admin = searchParams.get("admin") === "true";
    const superAdmin = searchParams.get("superAdmin") === "true";
    const storeAdmin = searchParams.get("storeAdmin") === "true";
    const manager = searchParams.get("manager") === "true";
    const ios = searchParams.get("ios") === "true";
    const android = searchParams.get("android") === "true";
    const biggestRevenuers = searchParams.get("biggestRevenuers") === "true";
    const biggestVolumeUsers =
      searchParams.get("biggestVolumeUsers") === "true";
    const withoutPostalcode = searchParams.get("withoutPostalcode") === "true";
    const withoutDrops5Days = searchParams.get("withoutDrops5Days") === "true";
    const materials = searchParams.get("materials");
    const kMeans = searchParams.get("kMeans");
    const dropCount = searchParams.get("dropCount") || "";
    const search = searchParams.get("search") ?? "";
    const showFiltersParam = searchParams.get("showFilters") === "true";

    if (admin) setShowAdmin(true);
    if (superAdmin) setShowSuperAdmin(true);
    if (storeAdmin) setShowStoreAdmin(true);
    if (manager) setShowManager(true);
    if (ios) setShowIOS(true);
    if (android) setShowAndroid(true);
    if (biggestRevenuers) setShowBiggestRevenuers(true);
    if (biggestVolumeUsers) setShowBiggestVolumeUsers(true);
    if (withoutPostalcode) setShowUsersWithoutPostalcode(true);
    if (withoutDrops5Days) setShowUsersWithoutDropsInLast5Days(true);
    if (materials) setSpecificMaterials(materials.split(","));
    if (kMeans) setSpecificKMeans(kMeans.split(",").map(Number));
    if (dropCount) setDropCountFilter(dropCount);
    setSearchText(search);
    if (showFiltersParam) setShowFilters(true);

    filtersLoadedFromUrl.current = true;
    setFiltersInitialized(true);
  }, []);

  useEffect(() => {
    getAllMarketPrices();
  }, []);

  useEffect(() => {
    if (!filtersInitialized) return;

    const params = new URLSearchParams();

    if (showAdmin) params.set("admin", "true");
    if (showSuperAdmin) params.set("superAdmin", "true");
    if (showStoreAdmin) params.set("storeAdmin", "true");
    if (showManager) params.set("manager", "true");
    if (showIOS) params.set("ios", "true");
    if (showAndroid) params.set("android", "true");
    if (showBiggestRevenuers) params.set("biggestRevenuers", "true");
    if (showBiggestVolumeUsers) params.set("biggestVolumeUsers", "true");
    if (showUsersWithoutPostalcode) params.set("withoutPostalcode", "true");
    if (showUsersWithoutDropsInLast5Days)
      params.set("withoutDrops5Days", "true");
    if (specificMaterials.length > 0)
      params.set("materials", specificMaterials.join(","));
    if (specificKMeans.length > 0)
      params.set("kMeans", specificKMeans.join(","));
    if (dropCountFilter) params.set("dropCount", dropCountFilter);
    if (searchText) params.set("search", searchText);
    if (showFilters) params.set("showFilters", "true");

    const newSearch = params.toString();
    const currentSearch = location.search.replace(/^\?/, "");
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;

    if (currentSearch !== newSearch) {
      navigate(newUrl, { replace: true });
    }
  }, [
    showAdmin,
    showSuperAdmin,
    showStoreAdmin,
    showManager,
    showIOS,
    showAndroid,
    showBiggestRevenuers,
    showBiggestVolumeUsers,
    showUsersWithoutPostalcode,
    showUsersWithoutDropsInLast5Days,
    specificMaterials,
    specificKMeans,
    dropCountFilter,
    searchText,
    showFilters,
    filtersInitialized,
    location.pathname,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    if (!filtersInitialized) return;

    if (searchText === "") {
      setPage(1);
      getAllUsers(
        1,
        20,
        "",
        userFilters,
        userSort,
        showUsersWithoutPostalcode,
        showUsersWithoutDropsInLast5Days,
        specificMaterials,
        dropCountFilter === "never",
        dropCountFilter === "onePlus",
        dropCountFilter === "twoPlus",
        dropCountFilter === "threePlus"
      );
      return;
    }
    if (searchTimeout) clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      setPage(1);
      getAllUsers(
        1,
        20,
        searchText,
        userFilters,
        userSort,
        showUsersWithoutPostalcode,
        showUsersWithoutDropsInLast5Days,
        specificMaterials,
        dropCountFilter === "never",
        dropCountFilter === "onePlus",
        dropCountFilter === "twoPlus",
        dropCountFilter === "threePlus"
      );
    }, 500);
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [
    searchText,
    userFilters,
    userSort,
    showUsersWithoutPostalcode,
    showUsersWithoutDropsInLast5Days,
    specificMaterials,
    dropCountFilter,
    filtersInitialized,
  ]);

  const clearFilters = () => {
    setShowAdmin(false);
    setShowSuperAdmin(false);
    setShowStoreAdmin(false);
    setShowManager(false);
    setShowIOS(false);
    setShowAndroid(false);
    setShowBiggestRevenuers(false);
    setShowBiggestVolumeUsers(false);
    setShowUsersWithoutPostalcode(false);
    setShowUsersWithoutDropsInLast5Days(false);
    setSpecificMaterials([]);
    setSpecificKMeans([]);
    setDropCountFilter("");
    navigate(location.pathname, { replace: true });
  };

  return (
    <div>
      <div className="flex w-full justify-between items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">Users</h1>
        <div className="flex">
          <PrimaryButton
            onClick={addUser}
            label="Add new"
            size="sm"
            className="h-10 mr-4 !bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
          />
          <PrimaryButton
                onClick={downloadUsersInCSV}
            label="Download CSV"
            size="sm"
            className="h-10 !bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
          />
        </div>
      </div>
      <div className="w-full px-7 py-3 bg-white rounded-xl shadow-none mb-3">
        <SearchInput
          value={searchText}
          onChange={handleSearch}
          placeholder="Name, email, phone, postcode, or user ID…"
        />
        <p className="mt-2 font-satoshi text-[11px] sm:text-xs text-tailgrids-text leading-snug break-words">
          Matches name (one or two words), email, phone, postcode, 24-character
          user ID 
        </p>
      </div>
      <div className="w-full px-7 py-4 bg-white rounded-xl shadow-none mb-3 flex flex-col items-start justify-center">
        <div className="flex w-full items-center justify-between gap-3 mb-2">
          <button
            type="button"
          onClick={() => setShowFilters((prev) => !prev)}
            className="font-satoshi font-semibold text-primary-base text-sm sm:text-base cursor-pointer underline-offset-4 hover:underline"
          >
            Advanced filters
            {activeFilters.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center rounded-full bg-droppie-oranje/10 px-2 py-0.5 text-[10px] font-medium text-droppie-oranje">
                {activeFilters.length}
              </span>
            )}
          </button>
          {activeFilters.length > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs sm:text-sm font-satoshi text-gray-500 hover:text-dark underline underline-offset-4"
            >
              Clear all
            </button>
          )}
        </div>
        {activeFilters.length > 0 && (
          <div className="w-full mb-1 flex flex-wrap gap-2">
            {activeFilters.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={f.clear}
                className="inline-flex items-center rounded-full border border-droppie-oranje/40 bg-droppie-oranje/5 px-3 py-1 text-xs sm:text-[13px] font-satoshi text-droppie-oranje hover:bg-droppie-oranje/10"
              >
                <span>{f.label}</span>
                <span className="ml-2 text-[10px] leading-none">&times;</span>
              </button>
            ))}
          </div>
        )}
        <div ref={advancedParent} className="w-full">
        {showFilters ? (
            <div className="w-full mt-4 space-y-5">
              {/* Row 1: Role, Platform, Sort, Drop count */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Role (multi) */}
                <div className="flex flex-col gap-1.5">
                  <label className="font-satoshi text-xs font-semibold uppercase tracking-wide text-tailgrids-text">
                    Role
                  </label>
                  <Menu>
                    <MenuHandler>
                      <button
                        type="button"
                        className="h-10 w-full px-3 border rounded-lg border-gray-border bg-white flex items-center justify-between font-satoshi font-medium text-dark text-sm hover:border-droppie-oranje/50 focus:outline-none focus:ring-2 focus:ring-droppie-oranje/20 transition-colors"
                      >
                        <span className="truncate min-w-0">
                          {[
                            showAdmin && "Admin",
                            showSuperAdmin && "Super admin",
                            showStoreAdmin && "Store admin",
                            showManager && "Manager",
                          ]
                            .filter(Boolean)
                            .join(", ") || "All roles"}
                        </span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-gray-500" />
                      </button>
                    </MenuHandler>
                    <MenuList
                      placeholder={undefined}
                      className="max-h-64 overflow-y-auto rounded-xl border border-gray-border shadow-lg"
                    >
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => setShowAdmin(!showAdmin)}
                        className="font-satoshi text-sm flex items-center gap-2"
                      >
                        <span
                          className={`w-4 h-4 rounded border flex items-center justify-center ${showAdmin ? "bg-[#0375FF] border-[#0375FF]" : "border-gray-300"}`}
                        >
                          {showAdmin && (
                            <span className="text-white text-xs">✓</span>
                          )}
                        </span>
                        Admin
                      </MenuItem>
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => setShowSuperAdmin(!showSuperAdmin)}
                        className="font-satoshi text-sm flex items-center gap-2"
                      >
                        <span
                          className={`w-4 h-4 rounded border flex items-center justify-center ${showSuperAdmin ? "bg-[#0375FF] border-[#0375FF]" : "border-gray-300"}`}
                        >
                          {showSuperAdmin && (
                            <span className="text-white text-xs">✓</span>
                          )}
                        </span>
                        Super admin
                      </MenuItem>
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => setShowStoreAdmin(!showStoreAdmin)}
                        className="font-satoshi text-sm flex items-center gap-2"
                      >
                        <span
                          className={`w-4 h-4 rounded border flex items-center justify-center ${showStoreAdmin ? "bg-[#0375FF] border-[#0375FF]" : "border-gray-300"}`}
                        >
                          {showStoreAdmin && (
                            <span className="text-white text-xs">✓</span>
                          )}
                        </span>
                        Store admin
                      </MenuItem>
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => setShowManager(!showManager)}
                        className="font-satoshi text-sm flex items-center gap-2"
                      >
                        <span
                          className={`w-4 h-4 rounded border flex items-center justify-center ${showManager ? "bg-[#0375FF] border-[#0375FF]" : "border-gray-300"}`}
                        >
                          {showManager && (
                            <span className="text-white text-xs">✓</span>
                          )}
                        </span>
                        Manager
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </div>

                {/* Platform (single) */}
                <div className="flex flex-col gap-1.5">
                  <label className="font-satoshi text-xs font-semibold uppercase tracking-wide text-tailgrids-text">
                    Platform
                  </label>
                  <Menu>
                    <MenuHandler>
                      <button
                        type="button"
                        className="h-10 w-full px-3 border rounded-lg border-gray-border bg-white flex items-center justify-between font-satoshi font-medium text-dark text-sm hover:border-droppie-oranje/50 focus:outline-none focus:ring-2 focus:ring-droppie-oranje/20 transition-colors"
                      >
                        <span>
                          {showIOS && showAndroid
                            ? "iOS & Android"
                            : showIOS
                              ? "iOS"
                              : showAndroid
                                ? "Android"
                                : "Any platform"}
                        </span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-gray-500" />
                      </button>
                    </MenuHandler>
                    <MenuList
                      placeholder={undefined}
                      className="rounded-xl border border-gray-border shadow-lg"
                    >
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => {
                          setShowIOS(false);
                          setShowAndroid(false);
                        }}
                        className="font-satoshi text-sm"
                      >
                        Any platform
                      </MenuItem>
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => {
                          setShowIOS(true);
                          setShowAndroid(false);
                        }}
                        className="font-satoshi text-sm"
                      >
                        iOS
                      </MenuItem>
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => {
                          setShowIOS(false);
                          setShowAndroid(true);
                        }}
                        className="font-satoshi text-sm"
                      >
                        Android
                      </MenuItem>
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => {
                          setShowIOS(true);
                          setShowAndroid(true);
                        }}
                        className="font-satoshi text-sm"
                      >
                        iOS & Android
                      </MenuItem>
                    </MenuList>
                  </Menu>
            </div>

                {/* Sort by (single) */}
                <div className="flex flex-col gap-1.5">
                  <label className="font-satoshi text-xs font-semibold uppercase tracking-wide text-tailgrids-text">
                    Sort by
                  </label>
                  <Menu>
                    <MenuHandler>
                      <button
                        type="button"
                        className="h-10 w-full px-3 border rounded-lg border-gray-border bg-white flex items-center justify-between font-satoshi font-medium text-dark text-sm hover:border-droppie-oranje/50 focus:outline-none focus:ring-2 focus:ring-droppie-oranje/20 transition-colors"
                      >
                        <span>
                          {showBiggestRevenuers
                            ? "Top revenue"
                            : showBiggestVolumeUsers
                              ? "Top volume"
                              : "Default"}
                        </span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-gray-500" />
                      </button>
                    </MenuHandler>
                    <MenuList
                      placeholder={undefined}
                      className="rounded-xl border border-gray-border shadow-lg"
                    >
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => {
                          setShowBiggestRevenuers(false);
                          setShowBiggestVolumeUsers(false);
                        }}
                        className="font-satoshi text-sm"
                      >
                        Default
                      </MenuItem>
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => {
                          setShowBiggestRevenuers(true);
                          setShowBiggestVolumeUsers(false);
                        }}
                        className="font-satoshi text-sm"
                      >
                        Top revenue
                      </MenuItem>
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => {
                          setShowBiggestRevenuers(false);
                          setShowBiggestVolumeUsers(true);
                        }}
                        className="font-satoshi text-sm"
                      >
                        Top volume
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </div>

                {/* Drop count (single) */}
                <div className="flex flex-col gap-1.5">
                  <label className="font-satoshi text-xs font-semibold uppercase tracking-wide text-tailgrids-text">
                    Drop count
                  </label>
                  <Menu>
                    <MenuHandler>
                      <button
                        type="button"
                        className="h-10 w-full px-3 border rounded-lg border-gray-border bg-white flex items-center justify-between font-satoshi font-medium text-dark text-sm hover:border-droppie-oranje/50 focus:outline-none focus:ring-2 focus:ring-droppie-oranje/20 transition-colors"
                      >
                        <span>
                          {dropCountFilter === "never"
                            ? "Never dropped"
                            : dropCountFilter === "onePlus"
                              ? "1+ drops"
                              : dropCountFilter === "twoPlus"
                                ? "2+ drops"
                                : dropCountFilter === "threePlus"
                                  ? "3+ drops"
                                  : "Any"}
                        </span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-gray-500" />
                      </button>
                    </MenuHandler>
                    <MenuList
                      placeholder={undefined}
                      className="rounded-xl border border-gray-border shadow-lg"
                    >
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => setDropCountFilter("")}
                        className="font-satoshi text-sm"
                      >
                        Any
                      </MenuItem>
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => setDropCountFilter("never")}
                        className="font-satoshi text-sm"
                      >
                        Never dropped
                      </MenuItem>
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => setDropCountFilter("onePlus")}
                        className="font-satoshi text-sm"
                      >
                        1+ drops
                      </MenuItem>
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => setDropCountFilter("twoPlus")}
                        className="font-satoshi text-sm"
                      >
                        2+ drops
                      </MenuItem>
                      <MenuItem
                        placeholder={undefined}
                        onClick={() => setDropCountFilter("threePlus")}
                        className="font-satoshi text-sm"
                      >
                        3+ drops
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </div>
              </div>

              {/* Row 2: Special, Segment, Material */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {/* Special (multi: no postal, no drops 5 days) */}
                <div className="flex flex-col gap-1.5">
                  <label className="font-satoshi text-xs font-semibold uppercase tracking-wide text-tailgrids-text">
                    Special filters
                  </label>
                  <Menu>
                    <MenuHandler>
                      <button
                        type="button"
                        className="h-10 w-full px-3 border rounded-lg border-gray-border bg-white flex items-center justify-between font-satoshi font-medium text-dark text-sm hover:border-droppie-oranje/50 focus:outline-none focus:ring-2 focus:ring-droppie-oranje/20 transition-colors"
                      >
                        <span className="truncate min-w-0">
                          {[
                            showUsersWithoutPostalcode && "No postal code",
                            showUsersWithoutDropsInLast5Days &&
                              "No drops 5 days",
                          ]
                            .filter(Boolean)
                            .join(", ") || "None"}
                        </span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-gray-500" />
                      </button>
                    </MenuHandler>
                    <MenuList
                      placeholder={undefined}
                      className="rounded-xl border border-gray-border shadow-lg"
                    >
                      <MenuItem
                        placeholder={undefined}
                        onClick={() =>
                          setShowUsersWithoutPostalcode(
                            !showUsersWithoutPostalcode
                          )
                        }
                        className="font-satoshi text-sm flex items-center gap-2"
                      >
                        <span
                          className={`w-4 h-4 rounded border flex items-center justify-center ${showUsersWithoutPostalcode ? "bg-[#0375FF] border-[#0375FF]" : "border-gray-300"}`}
                        >
                          {showUsersWithoutPostalcode && (
                            <span className="text-white text-xs">✓</span>
                          )}
                        </span>
                        No postal code
                      </MenuItem>
                      <MenuItem
                        placeholder={undefined}
                        onClick={() =>
                          setShowUsersWithoutDropsInLast5Days(
                            !showUsersWithoutDropsInLast5Days
                          )
                        }
                        className="font-satoshi text-sm flex items-center gap-2"
                      >
                        <span
                          className={`w-4 h-4 rounded border flex items-center justify-center ${showUsersWithoutDropsInLast5Days ? "bg-[#0375FF] border-[#0375FF]" : "border-gray-300"}`}
                        >
                          {showUsersWithoutDropsInLast5Days && (
                            <span className="text-white text-xs">✓</span>
                          )}
                        </span>
                        No drops in last 5 days
                      </MenuItem>
                    </MenuList>
                  </Menu>
            </div>

                {/* Segment (multi) */}
                <div className="flex flex-col gap-1.5">
                  <label className="font-satoshi text-xs font-semibold uppercase tracking-wide text-tailgrids-text">
                    Segment
                  </label>
                  <Menu>
                    <MenuHandler>
                      <button
                        type="button"
                        className="h-10 w-full px-3 border rounded-lg border-gray-border bg-white flex items-center justify-between font-satoshi font-medium text-dark text-sm hover:border-droppie-oranje/50 focus:outline-none focus:ring-2 focus:ring-droppie-oranje/20 transition-colors"
                      >
                        <span className="truncate">
                          {specificKMeans.length
                            ? `${specificKMeans.length} selected`
                            : "All segments"}
                        </span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-gray-500" />
                      </button>
                    </MenuHandler>
                    <MenuList
                      placeholder={undefined}
                      className="max-h-64 overflow-y-auto rounded-xl border border-gray-border shadow-lg"
                    >
                      {Object.entries(KMeanEnum)?.map(
                        ([value, label]: [string, string]) => {
                          const isChecked = specificKMeans.includes(
                            Number(value)
                          );
                          return (
                            <MenuItem
                              key={value}
                              placeholder={undefined}
                              onClick={() => {
                                if (isChecked) {
                                  setSpecificKMeans((prev) =>
                                    prev.filter((k) => k !== Number(value))
                                  );
                    } else {
                                  setSpecificKMeans((prev) => [
                                    ...prev,
                                    Number(value),
                                  ]);
                                }
                              }}
                              className="font-satoshi text-sm flex items-center gap-2"
                            >
                              <span
                                className={`w-4 h-4 rounded border flex items-center justify-center ${isChecked ? "bg-[#0375FF] border-[#0375FF]" : "border-gray-300"}`}
                              >
                                {isChecked && (
                                  <span className="text-white text-xs">✓</span>
                                )}
                              </span>
                              {label}
                            </MenuItem>
                          );
                        }
                      )}
                    </MenuList>
                  </Menu>
              </div>

                {/* Material (multi) */}
                <div className="flex flex-col gap-1.5">
                  <label className="font-satoshi text-xs font-semibold uppercase tracking-wide text-tailgrids-text">
                    Material
                  </label>
                  <Menu>
                    <MenuHandler>
                      <button
                        type="button"
                        className="h-10 w-full px-3 border rounded-lg border-gray-border bg-white flex items-center justify-between font-satoshi font-medium text-dark text-sm hover:border-droppie-oranje/50 focus:outline-none focus:ring-2 focus:ring-droppie-oranje/20 transition-colors"
                      >
                        <span className="truncate">
                          {specificMaterials.length
                            ? `${specificMaterials.length} selected`
                            : "All materials"}
                        </span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-gray-500" />
                      </button>
                    </MenuHandler>
                    <MenuList
                      placeholder={undefined}
                      className="max-h-64 overflow-y-auto rounded-xl border border-gray-border shadow-lg"
                    >
                      {!marketPrices?.length ? (
                        <div className="px-3 py-4 font-satoshi text-sm text-tailgrids-text">
                          No materials available
            </div>
                      ) : (
                        marketPrices.map((price) => {
                  const isChecked = specificMaterials.includes(
                    price._id as string
                  );
                          return (
                            <MenuItem
                              key={price._id}
                              placeholder={undefined}
                              onClick={() => {
                                if (isChecked) {
                                  setSpecificMaterials((prev) =>
                                    prev.filter((id) => id !== price._id)
                                  );
                                } else {
                                  setSpecificMaterials((prev) => [
                                    ...prev,
                        price._id as string,
                      ]);
                                }
                              }}
                              className="font-satoshi text-sm flex items-center gap-2"
                            >
                              <span
                                className={`w-4 h-4 rounded border flex items-center justify-center ${isChecked ? "bg-[#0375FF] border-[#0375FF]" : "border-gray-300"}`}
                              >
                                {isChecked && (
                                  <span className="text-white text-xs">✓</span>
                                )}
                              </span>
                              {price.name}
                            </MenuItem>
                          );
                        })
                      )}
                    </MenuList>
                  </Menu>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-row items-center justify-end gap-3 pt-2 border-gray-border">
              <SecondaryButton
                  label="Clear all"
                onClick={clearFilters}
                  className="h-9 px-4 bg-white border border-droppie-oranje text-droppie-oranje hover:bg-droppie-oranje/5"
                  labelClassName="text-droppie-oranje font-satoshi"
              />
              <PrimaryButton
                label="Close"
                onClick={() => setShowFilters(false)}
                  size="sm"
                  className="h-9 !bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark font-satoshi"
              />
            </div>
          </div>
        ) : null}
      </div>
      </div>
      <div className="w-full px-7 py-7 bg-white rounded-xl shadow-none">
        {allUsersLoading ? (
          <div className="flex items-center justify-center h-[240px] w-full">
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-gray-200 animate-spin fill-primary-base"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </div>
        ) : !allUsers?.length ? (
          <p className="text-center font-satoshi text-base text-dark">
            There is no user created yet.
          </p>
        ) : (
          <UsersTable />
        )}
      </div>
      {hasNextPage ? (
        <div className="flex flex-row w-full items-center justify-end mt-3">
          <PrimaryButton
            onClick={() => {
              if (searchText !== "") {
                setPage(page + 1);
                return getAllUsers(
                  page + 1,
                  20,
                  searchText,
                  userFilters,
                  userSort,
                  showUsersWithoutPostalcode,
                  showUsersWithoutDropsInLast5Days,
                  specificMaterials,
                  dropCountFilter === "never",
                  dropCountFilter === "onePlus",
                  dropCountFilter === "twoPlus",
                  dropCountFilter === "threePlus"
                );
              }
              setPage(page + 1);
              getAllUsers(
                page + 1,
                20,
                "",
                userFilters,
                userSort,
                showUsersWithoutPostalcode,
                showUsersWithoutDropsInLast5Days,
                specificMaterials,
                dropCountFilter === "never",
                dropCountFilter === "onePlus",
                dropCountFilter === "twoPlus",
                dropCountFilter === "threePlus"
              );
            }}
            label="Load more"
            className="h-10"
          />
        </div>
      ) : null}
    </div>
  );
}

export default withProtectedRoute(UsersScreen);
